/**
 * Zahlungs-Kennzahlen eines Auftrags (Bezahlt/Offen/Nächste Fälligkeit) + Kostenübernahme-Stand —
 * bisher zweimal fast identisch gerechnet: `event-detail.tsx` (Abrechnung-Kompaktkarte) UND
 * `abrechnung.tsx` (Zeile "Zahlungen"). WP2 (Auftrags-Dashboard, `docs/KONZEPT-*`-Plan
 * "Phase C.1") zieht das hier zusammen, EINE Stelle statt zwei fast identischer.
 *
 * Sonderregel "vollständig bezahlt" (M1, Bughunt 2026-07-11 + Z0.3 B-3): "Offen" heißt Geld noch
 * nicht eingegangen = Summe aller `pending`/`processing`-Zahlungen. Übersteigt bereits das
 * eingegangene Geld (`paidCents`) die Angebotssumme (z. B. weil eine spätere Rabatt-/
 * Preisänderung die Summe gesenkt hat, nachdem schon mehr gezahlt wurde), zeigt "Offen" trotzdem
 * 0 statt einer verwirrenden, technisch korrekten aber unsinnigen Restsumme — es ist ja bereits
 * mehr als genug eingegangen. Ohne Angebotssumme (`amountTotalCents` null/0) gilt die Regel nicht
 * (kein Bezugswert, an dem "vollständig" gemessen werden könnte).
 *
 * Anzahlungs-Fall (13.10.2026): Angebot 12.370 €, Anzahlung 2.474 € bezahlt, keine Restzahlung im
 * Plan — der Chip zeigte „Offen · 0,00 €“, obwohl 9.896 € fehlen. Seitdem gilt: MIT Angebotssumme
 * ist „Offen“ = Angebotssumme − eingegangen (nie negativ), egal ob dafür schon Zahlungszeilen
 * existieren. Ohne Angebotssumme bleibt es die Summe der offenen Zahlungszeilen.
 */
import { PAYMENT_TYPE_LABEL, formatDate, money, type PillTone } from "../ui/format";

export interface ZahlungLike {
  status: string;
  amountCents: number;
  dueDate?: string | null;
  /** Migration 484: bei Stripe teil-erstatteter Betrag (fehlt bei aelteren Antworten = 0). */
  refundedAmountCents?: number;
}

/**
 * Was von einer Zahlung als bezahlt zaehlt (Punkt 38, Antoine 17.09.2026: „Ja, nur 400 € gelten
 * als bezahlt."): der Betrag abzueglich Teil-Rueckerstattungen. Dieselbe Regel wie
 * `leseDeckung` in der API — nur fuer `succeeded`-Zeilen sinnvoll.
 */
export function nettoBezahltCents(p: { amountCents: number; refundedAmountCents?: number }): number {
  return p.amountCents - (p.refundedAmountCents ?? 0);
}

export interface ZahlungsKennzahlen {
  /** Summe aller `status === "succeeded"`-Zahlungen, abzueglich Teil-Rueckerstattungen. */
  paidCents: number;
  /** Mit Angebotssumme: Summe − bezahlt (>= 0). Ohne: Summe aller `pending`/`processing`-Zahlungen. */
  openCents: number;
  /** true, wenn die Sonderregel gegriffen hat (Anzeige "Vollständig bezahlt" statt Betrag). */
  vollstaendigBezahlt: boolean;
  /** ISO-Datum (yyyy-mm-dd) der nächsten offenen Fälligkeit, oder null ohne eine solche. */
  naechsteFaelligkeit: string | null;
}

export function berechneZahlungsKennzahlen(payments: ZahlungLike[], amountTotalCents: number | null): ZahlungsKennzahlen {
  const paidCents = payments.filter((p) => p.status === "succeeded").reduce((s, p) => s + nettoBezahltCents(p), 0);
  const openCentsRoh = payments
    .filter((p) => p.status === "pending" || p.status === "processing")
    .reduce((s, p) => s + p.amountCents, 0);
  const total = amountTotalCents ?? 0;
  const vollstaendigBezahlt = paidCents > 0 && total > 0 && paidCents >= total;
  const naechsteFaelligkeit =
    payments
      .filter((p) => (p.status === "pending" || p.status === "processing") && p.dueDate)
      .map((p) => p.dueDate as string)
      .sort()[0] ?? null;
  return {
    paidCents,
    openCents: total > 0 ? Math.max(0, total - paidCents) : openCentsRoh,
    vollstaendigBezahlt,
    naechsteFaelligkeit,
  };
}

/**
 * Zahlungsstand-Zeile fuer die Angebotsseite (Betreiber-Vorschau): „Anzahlung 2.474,00 € bezahlt
 * am 28.09.2026 · Rest 9.896,00 € offen“. `null`, solange nichts eingegangen ist.
 */
export function zahlungsstandZeile(
  payments: (ZahlungLike & { paymentType: string; paidAt?: string | null })[],
  sollCents: number | null,
): string | null {
  const bezahlt = payments.filter((p) => p.status === "succeeded" && nettoBezahltCents(p) > 0);
  if (bezahlt.length === 0) return null;
  const { paidCents, openCents } = berechneZahlungsKennzahlen(payments, sollCents);
  const einzeln = bezahlt.length === 1 ? bezahlt[0] : null;
  const teil = einzeln
    ? `${PAYMENT_TYPE_LABEL[einzeln.paymentType] ?? "Zahlung"} ${money(paidCents)} bezahlt${einzeln.paidAt ? ` am ${formatDate(einzeln.paidAt)}` : ""}`
    : `${money(paidCents)} bezahlt`;
  if (!sollCents || sollCents <= 0) return teil;
  return openCents > 0 ? `${teil} · Rest ${money(openCents)} offen` : `${teil} · vollständig bezahlt`;
}

/** Abrechnung Stufe 3 (sql/574): Soll/Bezahlt/Offen aus der abgeschlossenen Abrechnung, vom Server. */
export interface AbrechnungsDeckung {
  gesamtCents: number;
  bezahltCents: number;
  offenCents: number;
  gedeckt: boolean;
}

/**
 * Kennzahlen eines Vorgangs mit Maßstab: gibt es eine abgeschlossene Abrechnung, misst „Bezahlt/Offen“
 * an ihrem Betrag und zählt Zahlungen außerhalb von MAESTRO und Guthaben-Verrechnungen mit — die Zahlen
 * kommen fertig vom Server (`abrechnungDeckung`, dieselbe Funktion wie Statuswechsel und Webhook).
 * Sonst unverändert gegen die Angebotssumme. `sollCents` ist der jeweils geltende Maßstab.
 */
export function kennzahlenFuerVorgang(payments: ZahlungLike[], event: { amountTotalCents: number | null; abrechnungDeckung?: AbrechnungsDeckung | null }): ZahlungsKennzahlen & { sollCents: number | null; ausAbrechnung: boolean } {
  const d = event.abrechnungDeckung ?? null;
  if (!d) return { ...berechneZahlungsKennzahlen(payments, event.amountTotalCents), sollCents: event.amountTotalCents, ausAbrechnung: false };
  const basis = berechneZahlungsKennzahlen(payments, d.gesamtCents);
  return { ...basis, paidCents: d.bezahltCents, openCents: d.offenCents, vollstaendigBezahlt: d.gedeckt && d.bezahltCents > 0, sollCents: d.gesamtCents, ausAbrechnung: true };
}

/**
 * Punkt 38 (Antoine, 23.09.2026): „Nicht automatisch, der Betreiber soll gefragt werden. Bei einer
 * Überzahlung macht es keinen Sinn." — ein Vorgang auf 'paid', der nach einer Rückerstattung netto
 * NICHT mehr gedeckt ist, bekommt einen Hinweis mit Rückfrage statt eines stillen Statuswechsels.
 * Eine erstattete Überzahlung (netto weiterhin >= Summe) löst keinen Hinweis aus. `paidCents` ist
 * bereits netto (`berechneZahlungsKennzahlen`). Ohne Angebotssumme gibt es keinen Maßstab → null.
 */
export function erstattungsRueckfallHinweis(status: string, paidCents: number, amountTotalCents: number | null): string | null {
  const summe = amountTotalCents ?? 0;
  if (status !== "paid" || summe <= 0 || paidCents >= summe) return null;
  return `Nach einer Erstattung ist dieser Vorgang nicht mehr voll bezahlt (bezahlt ${money(paidCents)} von ${money(summe)}). Status zurücksetzen?`;
}

/**
 * Punkt 93 (Antoine, 23.09.2026): nach einer VOLLEN Rückerstattung (netto nichts mehr bezahlt)
 * heißt der Status „Erstattet“, nicht „Zahlung fehlgeschlagen“. Eine Teil-Erstattung unter die
 * Summe heißt seit Antoine 16.10.2026 „Restforderung offen“ (`balance_due`, Migration 726) —
 * vorher fiel sie auf „Zahlung fehlgeschlagen“, obwohl nichts fehlgeschlagen war.
 */
export function erstattungsRueckfallZiel(paidCents: number): "refunded" | "balance_due" {
  return paidCents <= 0 ? "refunded" : "balance_due";
}

/**
 * Punkt 93 (Antoine, 23.09.2026): „Wenn der Gast noch Geld schuldet, bekommt er keine Erstattung.
 * Er bekommt natürlich nur eine Erstattung, wenn er schlussendlich zu viel bezahlt hat, aus welchen
 * Gründen auch immer." — Was der Gast NACH dieser Erstattung noch schuldet (0 = Überzahlung oder
 * genau gedeckt → keine Rückfrage). Bewusst nur eine Rückfrage in der Oberfläche, keine Sperre im
 * Server: bei einer Absage steht die neue Summe oft noch nicht im System. Ohne Angebotssumme gibt
 * es keinen Maßstab → 0.
 */
export function schuldNachErstattungCents(paidNettoCents: number, amountTotalCents: number | null, erstattungCents: number): number {
  const summe = amountTotalCents ?? 0;
  if (summe <= 0) return 0;
  return Math.max(0, summe - (paidNettoCents - erstattungCents));
}

/** Spiegel der Statuswerte aus `apps/api/src/routes/cost-acceptance.ts`. */
export type KostenuebernahmeStatus = "draft" | "sent" | "signed" | "declined" | "withdrawn" | "signed_ungueltig";

const KOSTENUEBERNAHME_LABEL: Record<KostenuebernahmeStatus, { label: string; tone: PillTone }> = {
  sent: { label: "Gesendet – wartet auf Unterschrift", tone: "warn" },
  signed: { label: "Signiert – verbindlich", tone: "success" },
  declined: { label: "Abgelehnt", tone: "neutral" },
  withdrawn: { label: "Zurückgezogen", tone: "neutral" },
  // sql/770: unterschrieben, waehrend das Angebot nach dem Versand geaendert war - Nachweis, aber nicht verbindlich.
  signed_ungueltig: { label: "Unterschrieben, ungültig – Angebot wurde geändert", tone: "warn" },
  draft: { label: "Entwurf", tone: "neutral" },
};
const KOSTENUEBERNAHME_NICHT_ANGEFORDERT: { label: string; tone: PillTone } = { label: "Nicht angefordert", tone: "neutral" };

/**
 * Anzeige-Stand einer Kostenübernahme (`ca` = `GET /api/events/:id/cost-acceptance`-Antwort) —
 * reine Funktion, keine Netz-/State-Abhängigkeit. `null`/`undefined` heißt "noch nie angefordert".
 */
export function kostenuebernahmeStand(ca: { status: string } | null | undefined): { label: string; tone: PillTone } {
  if (!ca) return KOSTENUEBERNAHME_NICHT_ANGEFORDERT;
  return KOSTENUEBERNAHME_LABEL[ca.status as KostenuebernahmeStatus] ?? { label: ca.status, tone: "neutral" };
}

/**
 * WARTESCHLANGE (Antoine, 15./16.09.2026, HAT PRIO): "Wenn sich im Angebot etwas ändert, muss der
 * Betreiber eine neue Kostenübernahme schicken können." Sobald bereits IRGENDEINE Kostenübernahme
 * existiert, öffnet `abrechnung.tsx` denselben Anfordern-Dialog erneut (routes/cost-acceptance.ts
 * ersetzt/widerruft den Vorgänger serverseitig — H-42/M-23 für 'sent', der neue Reopening-Sonderfall
 * für 'signed'). Dieser Hinweistext macht im Dialog explizit, was mit dem BESTEHENDEN Vertrag
 * passiert, damit niemand annimmt, beide blieben parallel gültig (harte Vorgabe der Einheit).
 * Reine Anzeigefunktion, unabhängig testbar — kein Platzhalter/Raten für einen unbekannten Status.
 */
export function kostenuebernahmeNeuanforderungHinweis(status: string): string {
  switch (status as KostenuebernahmeStatus) {
    case "signed":
      // KÜ V2 (E1, Antoine 28.09.2026): ersetzt erst mit der Unterschrift, nicht mit dem Versand.
      return "Der bereits unterschriebene Vertrag bleibt gültig, bis der Kunde den neuen unterschreibt — erst dann ist er ersetzt. Er bleibt als Rechtsnachweis archiviert.";
    case "sent":
      return "Der zugestellte, noch nicht unterschriebene Vertrag wird beim Versand automatisch widerrufen (Signatur-Link wird ungültig) und durch diesen hier ersetzt.";
    case "declined":
      return "Der abgelehnte Vertrag bleibt zur Nachvollziehbarkeit erhalten — dieser hier geht als neue Anfrage an den Kunden.";
    case "withdrawn":
      return "Der widerrufene Vertrag bleibt zur Nachvollziehbarkeit erhalten — dieser hier geht als neue Anfrage an den Kunden.";
    default:
      return "Die bestehende Kostenübernahme wird durch diesen neuen Vertrag ersetzt.";
  }
}
